/**
 * Losi Planner Day Card
 *
 * Renders a single-day planner proposal inside chat messages.
 *
 * Primary responsibilities:
 * - Place planned events on an hour grid from marker payload data
 * - Let users drag, edit, approve, or cancel proposed placements
 * - Persist approved Space events when enough planner context is present
 *
 * Dependencies:
 * - Planner day marker payload types
 * - Pulse planner interaction helpers
 * - Authenticated browser fetch helper
 */

'use client'

import type { CSSProperties, PointerEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  IconCalendarTime,
  IconCheck,
  IconClock,
  IconGripVertical,
  IconMapPin,
  IconPencil,
  IconTrash,
  IconX
} from '@tabler/icons-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import type {
  LosiPlannerDayItemStatus,
  LosiPlannerDayMarkerItem,
  LosiPlannerDayMarkerPayload
} from '@/lib/planner-day-marker'
import type { SpaceHierarchyTask } from '@/lib/spaces/hierarchy-loader'
import {
  stackPulsePlannerTimedItems,
  type PulsePlannerStackedTimedItem
} from '@/lib/spaces/planner-interactions'
import { cn } from '@/lib/utils'
import type { SpaceCalendarFeedEvent } from '@/types/spaces'

export interface LosiPlannerDayCardProps {
  className?: string
  payload: LosiPlannerDayMarkerPayload
}

type EditablePlannerItem = LosiPlannerDayMarkerItem & {
  acceptedSnapshot?: LosiPlannerDayMarkerItem | null
  error?: string | null
  saving?: boolean
}

type DragState = {
  durationMinutes: number
  gridTop: number
  id: string
  moved: boolean
  pointerId: number
  previewEnd: string
  previewStart: string
  startClientY: number
}

type PlannerItemStyle = CSSProperties & {
  height: number
  top: number
}

type PlannerHydrationRange = {
  end: Date
  start: Date
}

const HOUR_HEIGHT = 64
const SLOT_MINUTES = 15
const DEFAULT_VIEW_START_HOUR = 9
const DEFAULT_VIEW_END_HOUR = 12
const MIN_VISIBLE_MINUTES = 3 * 60
const MAX_VISIBLE_MINUTES = 30 * 60
const MIN_ITEM_MINUTES = 15
const DEFAULT_HYDRATED_TASK_MINUTES = 60

const COMPLETED_TASK_STATUSES = new Set(['cancelled', 'complete', 'completed', 'done'])

const statusCopy = {
  accepted: 'Set',
  cancelled: 'Cancelled',
  proposed: 'Needs OK'
} satisfies Record<LosiPlannerDayItemStatus, string>

const sourceStyles = {
  accepted:
    'border-emerald-300/80 bg-emerald-50 text-emerald-950 shadow-emerald-900/5 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-50',
  cancelled:
    'border-border bg-muted/60 text-muted-foreground opacity-70',
  proposed:
    'border-amber-300/90 bg-amber-50 text-amber-950 shadow-amber-900/10 dark:border-amber-500/50 dark:bg-amber-500/15 dark:text-amber-50'
} satisfies Record<LosiPlannerDayItemStatus, string>

const statusIconStyles = {
  accepted:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-100',
  cancelled:
    'bg-muted text-muted-foreground',
  proposed:
    'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-100'
} satisfies Record<LosiPlannerDayItemStatus, string>

// --------------

function getPlannerItemSnapshot(
  item: LosiPlannerDayMarkerItem,
  status: LosiPlannerDayItemStatus
): LosiPlannerDayMarkerItem {
  return {
    ...(item.allDay === undefined ? {} : { allDay: item.allDay }),
    ...(item.description ? { description: item.description } : {}),
    end: item.end,
    ...(item.eventId ? { eventId: item.eventId } : {}),
    id: item.id,
    kind: item.kind,
    ...(item.location ? { location: item.location } : {}),
    source: item.source,
    ...(item.spaceId ? { spaceId: item.spaceId } : {}),
    start: item.start,
    status,
    ...(item.taskId ? { taskId: item.taskId } : {}),
    title: item.title
  }
}

function getPlannerStatusIcon(status: LosiPlannerDayItemStatus, className: string) {
  if (status === 'accepted') {
    return <IconCheck aria-hidden="true" className={className} />
  }

  if (status === 'cancelled') {
    return <IconX aria-hidden="true" className={className} />
  }

  return <IconClock aria-hidden="true" className={className} />
}

function normalizeItems(payload: LosiPlannerDayMarkerPayload): EditablePlannerItem[] {
  const usedIds = new Set<string>()

  return payload.items.map((item, index) => {
    const baseId = item.id || item.eventId || item.taskId || `planned-${index + 1}`
    const id = usedIds.has(baseId) ? `${baseId}-${index + 1}` : baseId

    usedIds.add(id)

    const normalizedItem = {
      ...item,
      id
    }

    return {
      ...normalizedItem,
      acceptedSnapshot:
        normalizedItem.status === 'accepted'
          ? getPlannerItemSnapshot(normalizedItem, 'accepted')
          : null
    }
  })
}

function getPlannerPayloadKey(payload: LosiPlannerDayMarkerPayload) {
  return JSON.stringify(payload)
}

function parseDayDate(dateKey: string) {
  const parsed = new Date(`${dateKey}T00:00:00`)

  return Number.isFinite(parsed.getTime()) ? parsed : new Date()
}

function getDayStart(date: Date) {
  const nextDate = new Date(date)
  nextDate.setHours(0, 0, 0, 0)

  return nextDate
}

function getNextDayStart(date: Date) {
  const nextDate = getDayStart(date)
  nextDate.setDate(nextDate.getDate() + 1)

  return nextDate
}

function getDateKey(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function formatDayLabel(date: Date) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'long',
    weekday: 'long'
  }).format(date)
}

function formatTime(date: Date) {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  }).format(date)
}

function formatRangeTime(date: Date, rangeStart: Date) {
  const label = formatTime(date)

  return getDateKey(date) === getDateKey(rangeStart)
    ? label
    : `${label} next day`
}

function formatTimeRange(startDate: Date, endDate: Date) {
  const endLabel = getDateKey(endDate) === getDateKey(startDate)
    ? formatTime(endDate)
    : `${formatTime(endDate)} next day`

  return `${formatTime(startDate)} - ${endLabel}`
}

function toTimeInputValue(value: string) {
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return ''

  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

function getItemMinutes(item: Pick<EditablePlannerItem, 'end' | 'start'>) {
  const start = new Date(item.start)
  const end = new Date(item.end)
  const duration = Math.round((end.getTime() - start.getTime()) / 60_000)

  return Math.max(MIN_ITEM_MINUTES, Number.isFinite(duration) ? duration : 60)
}

function setItemTime(item: EditablePlannerItem, dayDate: Date, key: 'end' | 'start', value: string) {
  const match = value.match(/^([01]\d|2[0-3]):([0-5]\d)$/)
  if (!match) return item

  const nextDate = new Date(dayDate)
  nextDate.setHours(Number(match[1]), Number(match[2]), 0, 0)

  const nextItem = {
    ...item,
    [key]: nextDate.toISOString(),
    status: 'proposed' as const
  }

  if (key === 'end' && new Date(nextItem.end).getTime() <= new Date(nextItem.start).getTime()) {
    const nextEnd = new Date(nextItem.end)
    nextEnd.setDate(nextEnd.getDate() + 1)
    nextItem.end = nextEnd.toISOString()
  }

  if (new Date(nextItem.end).getTime() <= new Date(nextItem.start).getTime()) {
    const startDate = new Date(nextItem.start)
    nextItem.end = new Date(startDate.getTime() + getItemMinutes(item) * 60_000).toISOString()
  }

  return nextItem
}

type PlannerViewRange = {
  end: Date
  start: Date
  timeSlots: Date[]
  visibleMinutes: number
}

function floorToHour(date: Date) {
  const nextDate = new Date(date)
  nextDate.setMinutes(0, 0, 0)

  return nextDate
}

function ceilToHour(date: Date) {
  const nextDate = floorToHour(date)
  if (nextDate.getTime() < date.getTime()) {
    nextDate.setHours(nextDate.getHours() + 1)
  }

  return nextDate
}

function getDefaultViewStart(dayDate: Date) {
  const start = new Date(dayDate)
  start.setHours(DEFAULT_VIEW_START_HOUR, 0, 0, 0)

  return start
}

function getDefaultViewEnd(dayDate: Date) {
  const end = new Date(dayDate)
  end.setHours(DEFAULT_VIEW_END_HOUR, 0, 0, 0)

  return end
}

function getValidDate(value: string | null | undefined) {
  if (!value) return null

  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? date : null
}

function getPlannerHydrationRange(
  dayDate: Date,
  payload: LosiPlannerDayMarkerPayload
): PlannerHydrationRange {
  const dayStart = getDayStart(dayDate)
  const dayEnd = getNextDayStart(dayDate)
  const viewStart = getValidDate(payload.viewStart)
  const viewEnd = getValidDate(payload.viewEnd)

  return {
    end: new Date(Math.max(dayEnd.getTime(), viewEnd?.getTime() ?? dayEnd.getTime())),
    start: new Date(Math.min(dayStart.getTime(), viewStart?.getTime() ?? dayStart.getTime()))
  }
}

function normalizeViewRange(start: Date, end: Date): PlannerViewRange {
  const rangeStart = floorToHour(start)
  let rangeEnd = ceilToHour(end)

  const visibleMinutes = Math.round(
    (rangeEnd.getTime() - rangeStart.getTime()) / 60_000
  )
  if (visibleMinutes < MIN_VISIBLE_MINUTES) {
    rangeEnd = new Date(rangeStart.getTime() + MIN_VISIBLE_MINUTES * 60_000)
  } else if (visibleMinutes > MAX_VISIBLE_MINUTES) {
    rangeEnd = new Date(rangeStart.getTime() + MAX_VISIBLE_MINUTES * 60_000)
  }

  const nextVisibleMinutes = Math.round(
    (rangeEnd.getTime() - rangeStart.getTime()) / 60_000
  )
  const timeSlots = Array.from(
    { length: Math.ceil(nextVisibleMinutes / 60) },
    (_, index) => new Date(rangeStart.getTime() + index * 60 * 60_000)
  )

  return {
    end: rangeEnd,
    start: rangeStart,
    timeSlots,
    visibleMinutes: nextVisibleMinutes
  }
}

function getPlannerViewRange(
  dayDate: Date,
  items: EditablePlannerItem[],
  payload: LosiPlannerDayMarkerPayload
) {
  const explicitStart = getValidDate(payload.viewStart)
  const explicitEnd = getValidDate(payload.viewEnd)
  if (explicitStart && explicitEnd && explicitEnd.getTime() > explicitStart.getTime()) {
    return normalizeViewRange(explicitStart, explicitEnd)
  }

  const timedDates = items.flatMap((item) => {
    if (item.allDay || item.status === 'cancelled') return []

    const start = new Date(item.start)
    const end = new Date(item.end)

    return Number.isFinite(start.getTime()) && Number.isFinite(end.getTime())
      ? [{ end, start }]
      : []
  })

  if (timedDates.length > 0) {
    const firstStart = new Date(
      Math.min(...timedDates.map((item) => item.start.getTime()))
    )
    const lastEnd = new Date(
      Math.max(...timedDates.map((item) => item.end.getTime()))
    )

    return normalizeViewRange(firstStart, lastEnd)
  }

  return normalizeViewRange(getDefaultViewStart(dayDate), getDefaultViewEnd(dayDate))
}

function getViewRangeLabel(viewRange: PlannerViewRange) {
  return `${formatRangeTime(viewRange.start, viewRange.start)} - ${formatRangeTime(
    viewRange.end,
    viewRange.start
  )}`
}

function getCurrentTimeTop(now: Date, viewRange: PlannerViewRange) {
  if (
    now.getTime() < viewRange.start.getTime() ||
    now.getTime() > viewRange.end.getTime()
  ) {
    return null
  }

  return ((now.getTime() - viewRange.start.getTime()) / 60_000 / 60) * HOUR_HEIGHT
}

function getItemStyle(
  item: EditablePlannerItem,
  viewRange: PlannerViewRange,
  stackedItem?: PulsePlannerStackedTimedItem<EditablePlannerItem>
): PlannerItemStyle | null {
  if (item.allDay) return null

  const startDate = new Date(item.start)
  const endDate = new Date(item.end)
  if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime())) return null

  const startMs = Math.max(startDate.getTime(), viewRange.start.getTime())
  const endMs = Math.min(endDate.getTime(), viewRange.end.getTime())
  if (endMs <= viewRange.start.getTime() || startMs >= viewRange.end.getTime()) return null

  const laneStart = stackedItem?.laneStartPercent ?? 0
  const laneWidth = stackedItem?.laneWidthPercent ?? 100
  const top = ((startMs - viewRange.start.getTime()) / 60_000 / 60) * HOUR_HEIGHT
  const height = ((endMs - startMs) / 60_000 / 60) * HOUR_HEIGHT

  return {
    height,
    left: `calc(${laneStart}% + 4px)`,
    top,
    width: `calc(${laneWidth}% - 8px)`
  }
}

function getSlotStart(viewRange: PlannerViewRange, offsetY: number, durationMinutes: number) {
  const rawMinutes = (Math.max(0, offsetY) / HOUR_HEIGHT) * 60
  const snappedMinutes = Math.round(rawMinutes / SLOT_MINUTES) * SLOT_MINUTES
  const latestStart = Math.max(0, viewRange.visibleMinutes - durationMinutes)
  const minutes = Math.min(Math.max(snappedMinutes, 0), latestStart)
  const start = new Date(viewRange.start.getTime() + minutes * 60_000)

  return start
}

function getDragPreviewDates(
  clientY: number,
  gridTop: number,
  viewRange: PlannerViewRange,
  durationMinutes: number
) {
  const start = getSlotStart(viewRange, clientY - gridTop, durationMinutes)
  const end = new Date(start.getTime() + durationMinutes * 60_000)

  return { end, start }
}

function isReadOnlyItem(item: EditablePlannerItem) {
  return item.source === 'apple' || item.source === 'google'
}

function isCompletedTask(task: SpaceHierarchyTask) {
  return COMPLETED_TASK_STATUSES.has(task.status?.trim().toLowerCase() ?? '')
}

function getPersistedEventId(value: unknown) {
  return value && typeof value === 'object' && 'id' in value && typeof value.id === 'string'
    ? value.id
    : null
}

function getPlannerEventSpaceId(
  item: Pick<EditablePlannerItem, 'spaceId'>,
  payload: Pick<LosiPlannerDayMarkerPayload, 'spaceId'>
) {
  return item.spaceId ?? payload.spaceId ?? null
}

function canPersistPlannerEvent(
  item: EditablePlannerItem,
  payload: Pick<LosiPlannerDayMarkerPayload, 'spaceId'>
) {
  return Boolean(item.eventId && getPlannerEventSpaceId(item, payload) && !isReadOnlyItem(item))
}

function canDeletePlannerEvent(
  item: EditablePlannerItem,
  payload: Pick<LosiPlannerDayMarkerPayload, 'spaceId'>
) {
  return item.status === 'accepted' && canPersistPlannerEvent(item, payload)
}

function getPlannerEventRequestBody(
  item: EditablePlannerItem,
  payload: Pick<LosiPlannerDayMarkerPayload, 'timezone'>,
  eventId: string | undefined
) {
  return {
    all_day: Boolean(item.allDay),
    description: item.description ?? null,
    end_time: item.end,
    location: item.location ?? null,
    replace_task_linked_event: Boolean(item.taskId && !eventId),
    start_time: item.start,
    task_id: item.taskId ?? null,
    timezone: payload.timezone ?? null,
    title: item.title
  }
}

function getHydratedEventEnd(event: SpaceCalendarFeedEvent, start: Date) {
  const end = getValidDate(event.end_date)

  return end && end.getTime() >= start.getTime()
    ? end
    : new Date(start.getTime() + DEFAULT_HYDRATED_TASK_MINUTES * 60_000)
}

function getCalendarEventPlannerSource(
  event: SpaceCalendarFeedEvent
): Extract<LosiPlannerDayMarkerItem['source'], 'apple' | 'google' | 'space'> {
  if (event.source === 'apple' || event.source === 'google') {
    return event.source
  }

  return 'space'
}

function getHydratedEventPlannerItem(
  event: SpaceCalendarFeedEvent,
  spaceId: string
): EditablePlannerItem | null {
  const start = getValidDate(event.start_date)
  if (!start) return null

  const end = getHydratedEventEnd(event, start)
  const item: EditablePlannerItem = {
    ...(event.all_day === undefined ? {} : { allDay: event.all_day }),
    ...(event.description ? { description: event.description } : {}),
    end: end.toISOString(),
    eventId: event.id,
    id: `event-${event.id}`,
    kind: event.task_id ? 'task' : 'event',
    ...(event.location ? { location: event.location } : {}),
    source: getCalendarEventPlannerSource(event),
    spaceId,
    start: start.toISOString(),
    status: 'accepted',
    ...(event.task_id ? { taskId: event.task_id } : {}),
    title: event.title
  }

  return {
    ...item,
    acceptedSnapshot: getPlannerItemSnapshot(item, 'accepted')
  }
}

function parseTaskDateTime(dateValue: string | null, timeValue: string | null) {
  if (!dateValue?.trim()) return null

  const trimmedDate = dateValue.trim()
  const trimmedTime = timeValue?.trim()
  const parsed = trimmedTime
    ? new Date(`${trimmedDate}T${trimmedTime}`)
    : new Date(trimmedDate)

  return Number.isFinite(parsed.getTime()) ? parsed : null
}

function getTaskScheduleStart(task: SpaceHierarchyTask) {
  const startDate = getValidDate(task.start_date)
  if (startDate) return startDate

  return parseTaskDateTime(task.due_date, task.due_time)
}

function getHydratedTaskPlannerItem(
  task: SpaceHierarchyTask,
  spaceId: string,
  range: PlannerHydrationRange
): EditablePlannerItem | null {
  if (isCompletedTask(task)) return null

  const start = getTaskScheduleStart(task)
  if (!start) return null

  const durationMinutes = Math.max(
    MIN_ITEM_MINUTES,
    task.time_estimate_minutes ?? DEFAULT_HYDRATED_TASK_MINUTES
  )
  const end = new Date(start.getTime() + durationMinutes * 60_000)
  if (!doesDateRangeOverlap(start, end, range)) return null

  const hasSpecificTime = Boolean(task.start_date || task.due_time)
  const item: EditablePlannerItem = {
    ...(hasSpecificTime ? {} : { allDay: true }),
    ...(task.description ? { description: task.description } : {}),
    end: end.toISOString(),
    id: `task-${task.id}`,
    kind: 'task',
    source: 'task',
    spaceId,
    start: start.toISOString(),
    status: 'accepted',
    taskId: task.id,
    title: task.title
  }

  return {
    ...item,
    acceptedSnapshot: getPlannerItemSnapshot(item, 'accepted')
  }
}

function doesDateRangeOverlap(start: Date, end: Date, range: PlannerHydrationRange) {
  return start.getTime() < range.end.getTime() && end.getTime() > range.start.getTime()
}

function getPlannerItemTitleKey(item: Pick<LosiPlannerDayMarkerItem, 'title'>) {
  return item.title.trim().toLowerCase().replace(/\s+/g, ' ')
}

function getRelaxedPlannerTitleKey(item: Pick<LosiPlannerDayMarkerItem, 'title'>) {
  return getPlannerItemTitleKey(item)
    .replace(/^(focus|event|task|block):\s+/, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function getPlannerTitleTokens(item: Pick<LosiPlannerDayMarkerItem, 'title'>) {
  return getRelaxedPlannerTitleKey(item)
    .split(' ')
    .filter((token) => token.length > 2 && token !== 'the' && token !== 'and')
}

function doPlannerTitlesLookRelated(
  left: Pick<LosiPlannerDayMarkerItem, 'title'>,
  right: Pick<LosiPlannerDayMarkerItem, 'title'>
) {
  const leftTitle = getRelaxedPlannerTitleKey(left)
  const rightTitle = getRelaxedPlannerTitleKey(right)
  if (!leftTitle || !rightTitle) return false
  if (leftTitle === rightTitle || leftTitle.includes(rightTitle) || rightTitle.includes(leftTitle)) {
    return true
  }

  const rightTokens = new Set(getPlannerTitleTokens(right))
  const sharedTokenCount = getPlannerTitleTokens(left).filter((token) => rightTokens.has(token))
    .length

  return sharedTokenCount >= 2
}

function isSamePlannerDay(left: LosiPlannerDayMarkerItem, right: LosiPlannerDayMarkerItem) {
  return getDateKey(new Date(left.start)) === getDateKey(new Date(right.start))
}

function isSamePlannerTime(left: LosiPlannerDayMarkerItem, right: LosiPlannerDayMarkerItem) {
  return new Date(left.start).getTime() === new Date(right.start).getTime() &&
    new Date(left.end).getTime() === new Date(right.end).getTime()
}

function getPlannerOverlapMinutes(left: LosiPlannerDayMarkerItem, right: LosiPlannerDayMarkerItem) {
  const start = Math.max(new Date(left.start).getTime(), new Date(right.start).getTime())
  const end = Math.min(new Date(left.end).getTime(), new Date(right.end).getTime())
  const overlap = Math.round((end - start) / 60_000)

  return Number.isFinite(overlap) ? Math.max(0, overlap) : 0
}

function doesHydratedItemMatchProposal(
  proposal: EditablePlannerItem,
  hydrated: EditablePlannerItem
) {
  if (proposal.eventId && hydrated.eventId === proposal.eventId) return true
  if (proposal.taskId && hydrated.taskId === proposal.taskId) {
    return isSamePlannerTime(proposal, hydrated) ||
      (
        isSamePlannerDay(proposal, hydrated) &&
        doPlannerTitlesLookRelated(proposal, hydrated)
      )
  }

  if (getPlannerItemTitleKey(proposal) === getPlannerItemTitleKey(hydrated)) {
    return isSamePlannerTime(proposal, hydrated) || isSamePlannerDay(proposal, hydrated)
  }

  return isSamePlannerDay(proposal, hydrated) &&
    getPlannerOverlapMinutes(proposal, hydrated) > 0 &&
    doPlannerTitlesLookRelated(proposal, hydrated)
}

function canFuzzyMatchHydratedItem(item: EditablePlannerItem, hydrated: EditablePlannerItem) {
  if (item.eventId && hydrated.eventId && item.eventId !== hydrated.eventId) return false
  if (item.taskId && hydrated.taskId && item.taskId !== hydrated.taskId) return false

  return item.source === 'losi' || (!item.eventId && !item.taskId)
}

function mergeHydratedPlannerItems(
  currentItems: EditablePlannerItem[],
  hydratedItems: EditablePlannerItem[]
) {
  const usedHydratedIds = new Set<string>()
  const nextItems = currentItems.map((item) => {
    const hydratedItem = hydratedItems.find((candidate) => {
      return !usedHydratedIds.has(candidate.id) &&
        doesHydratedItemMatchProposal(item, candidate)
    })

    if (!hydratedItem) return item

    usedHydratedIds.add(hydratedItem.id)

    const acceptedItem: EditablePlannerItem = {
      ...item,
      ...(hydratedItem.allDay === undefined ? {} : { allDay: hydratedItem.allDay }),
      ...(hydratedItem.description ? { description: hydratedItem.description } : {}),
      end: hydratedItem.end,
      error: null,
      ...(hydratedItem.eventId ? { eventId: hydratedItem.eventId } : {}),
      ...(hydratedItem.location ? { location: hydratedItem.location } : {}),
      saving: false,
      source: hydratedItem.source,
      spaceId: hydratedItem.spaceId,
      start: hydratedItem.start,
      status: 'accepted',
      ...(hydratedItem.taskId ? { taskId: hydratedItem.taskId } : {}),
      title: hydratedItem.title
    }

    return {
      ...acceptedItem,
      acceptedSnapshot: getPlannerItemSnapshot(acceptedItem, 'accepted')
    }
  })
  const knownItemKeys = new Set(
    nextItems.map((item) => [
      item.eventId ?? '',
      item.taskId ?? '',
      getPlannerItemTitleKey(item),
      getDateKey(new Date(item.start))
    ].join(':'))
  )

  for (const hydratedItem of hydratedItems) {
    if (usedHydratedIds.has(hydratedItem.id)) continue
    if (
      nextItems.some((item) => (
        canFuzzyMatchHydratedItem(item, hydratedItem) &&
        doesHydratedItemMatchProposal(item, hydratedItem)
      ))
    ) {
      continue
    }

    const itemKey = [
      hydratedItem.eventId ?? '',
      hydratedItem.taskId ?? '',
      getPlannerItemTitleKey(hydratedItem),
      getDateKey(new Date(hydratedItem.start))
    ].join(':')

    if (knownItemKeys.has(itemKey)) continue

    knownItemKeys.add(itemKey)
    nextItems.push(hydratedItem)
  }

  return nextItems
}

async function fetchPlannerCalendarItems(args: {
  range: PlannerHydrationRange
  signal: AbortSignal
  spaceId: string
}) {
  const params = new URLSearchParams({
    end: args.range.end.toISOString(),
    start: args.range.start.toISOString()
  })
  const response = await authenticatedFetch(
    `/api/spaces/${args.spaceId}/calendar-feed?${params.toString()}`,
    {
      cache: 'no-store',
      signal: args.signal
    }
  )
  if (!response.ok) return []

  const payload = await response.json() as { events?: SpaceCalendarFeedEvent[] }
  const events = Array.isArray(payload.events) ? payload.events : []

  return events.flatMap((event) => {
    const item = getHydratedEventPlannerItem(event, args.spaceId)

    return item ? [item] : []
  })
}

async function fetchPlannerTaskItems(args: {
  range: PlannerHydrationRange
  signal: AbortSignal
  spaceId: string
}) {
  const response = await authenticatedFetch(
    `/api/spaces/${args.spaceId}/hierarchy?taskScope=all`,
    {
      cache: 'no-store',
      signal: args.signal
    }
  )
  if (!response.ok) return []

  const payload = await response.json() as { tasks?: SpaceHierarchyTask[] }
  const tasks = Array.isArray(payload.tasks) ? payload.tasks : []

  return tasks.flatMap((task) => {
    const item = getHydratedTaskPlannerItem(task, args.spaceId, args.range)

    return item ? [item] : []
  })
}

async function fetchHydratedPlannerItems(args: {
  range: PlannerHydrationRange
  signal: AbortSignal
  spaceId: string
}) {
  const [eventItems, taskItems] = await Promise.all([
    fetchPlannerCalendarItems(args).catch(() => []),
    fetchPlannerTaskItems(args).catch(() => [])
  ])

  return [...eventItems, ...taskItems]
}

// --------------

export function LosiPlannerDayCard({ className, payload }: LosiPlannerDayCardProps) {
  const initialItemsCacheRef = useRef<{
    items: EditablePlannerItem[]
    key: string
  } | null>(null)
  const payloadKey = getPlannerPayloadKey(payload)
  const initialItems = useMemo(() => {
    let cachedItems = initialItemsCacheRef.current

    if (cachedItems?.key !== payloadKey) {
      cachedItems = {
        items: normalizeItems(payload),
        key: payloadKey
      }
      initialItemsCacheRef.current = cachedItems
    }

    return cachedItems.items
  }, [payload, payloadKey])
  const initialItemsById = useMemo(
    () => new Map(initialItems.map((item) => [item.id, item])),
    [initialItems]
  )
  const dayDate = useMemo(() => parseDayDate(payload.date), [payload.date])
  const hydrationRange = useMemo(
    () => getPlannerHydrationRange(dayDate, payload),
    [dayDate, payload.viewEnd, payload.viewStart]
  )
  const spaceId = useMemo(
    () => payload.spaceId ?? initialItems.find((item) => item.spaceId)?.spaceId,
    [initialItems, payload.spaceId]
  )
  const [items, setItems] = useState<EditablePlannerItem[]>(initialItems)
  const [activeItemId, setActiveItemId] = useState<string | null>(null)
  const [approvingAll, setApprovingAll] = useState(false)
  const [dragState, setDragState] = useState<DragState | null>(null)
  const [now, setNow] = useState(() => new Date())
  const gridRef = useRef<HTMLDivElement | null>(null)
  const dragStateRef = useRef<DragState | null>(null)
  const itemsRef = useRef<EditablePlannerItem[]>(initialItems)
  const recentlyMovedEventIdsRef = useRef<Map<string, number>>(new Map())
  const suppressOpenForItemRef = useRef<string | null>(null)
  const visibleItems = items.filter((item) => item.status !== 'cancelled')
  const allDayItems = visibleItems.filter((item) => item.allDay)
  const timedItems = visibleItems.filter((item) => !item.allDay)
  const viewRange = useMemo(
    () => getPlannerViewRange(dayDate, [...initialItems, ...items], payload),
    [dayDate, initialItems, items, payload]
  )
  const gridHeight = (viewRange.visibleMinutes / 60) * HOUR_HEIGHT
  const stackedItems = useMemo(
    () =>
      stackPulsePlannerTimedItems(timedItems, (item) => ({
        end: new Date(item.end),
        id: item.id,
        start: new Date(item.start)
      })),
    [timedItems]
  )
  const stackedItemsById = useMemo(
    () => new Map(stackedItems.map((item) => [item.id, item])),
    [stackedItems]
  )
  const currentTimeTop = getCurrentTimeTop(now, viewRange)
  const proposedCount = visibleItems.filter((item) => item.status === 'proposed').length
  const acceptedCount = visibleItems.filter((item) => item.status === 'accepted').length
  const approvableCount = visibleItems.filter(
    (item) => item.status === 'proposed' && !isReadOnlyItem(item)
  ).length

  useEffect(() => {
    itemsRef.current = initialItems
    setItems(initialItems)
  }, [initialItems])

  useEffect(() => {
    itemsRef.current = items
  }, [items])

  useEffect(() => {
    dragStateRef.current = dragState
  }, [dragState])

  useEffect(() => {
    if (!spaceId) return

    const controller = new AbortController()

    void fetchHydratedPlannerItems({
      range: hydrationRange,
      signal: controller.signal,
      spaceId
    }).then((hydratedItems) => {
      if (controller.signal.aborted || hydratedItems.length === 0 || dragStateRef.current) return

      setItems((currentItems) => {
        if (currentItems.some((item) => item.saving)) {
          return currentItems
        }

        const now = Date.now()
        const filteredHydratedItems = hydratedItems.filter((hydratedItem) => {
          if (!hydratedItem.eventId) return true

          const guardedUntil = recentlyMovedEventIdsRef.current.get(hydratedItem.eventId)
          if (!guardedUntil) return true

          if (guardedUntil <= now) {
            recentlyMovedEventIdsRef.current.delete(hydratedItem.eventId)
            return true
          }

          return !currentItems.some((item) => item.eventId === hydratedItem.eventId)
        })
        const nextItems = mergeHydratedPlannerItems(currentItems, filteredHydratedItems)

        itemsRef.current = nextItems

        return nextItems
      })
    })

    return () => controller.abort()
  }, [hydrationRange, spaceId])

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(new Date()), 60_000)

    return () => window.clearInterval(intervalId)
  }, [])

  const updateItem = (id: string, updater: (item: EditablePlannerItem) => EditablePlannerItem) => {
    setItems((currentItems) => {
      const nextItems = currentItems.map((item) => (item.id === id ? updater(item) : item))

      itemsRef.current = nextItems

      return nextItems
    })
  }

  const updateDragState = (nextDragState: DragState | null) => {
    dragStateRef.current = nextDragState
    setDragState(nextDragState)
  }

  const handlePointerDown = (
    event: PointerEvent<HTMLButtonElement>,
    item: EditablePlannerItem
  ) => {
    if (event.button !== 0 || isReadOnlyItem(item)) return
    if ((event.target as HTMLElement).closest('[data-planner-action]')) return

    const grid = gridRef.current
    if (!grid) return

    const gridRect = grid.getBoundingClientRect()

    event.currentTarget.setPointerCapture(event.pointerId)
    setActiveItemId(null)
    updateDragState({
      durationMinutes: getItemMinutes(item),
      gridTop: gridRect.top,
      id: item.id,
      moved: false,
      pointerId: event.pointerId,
      previewEnd: item.end,
      previewStart: item.start,
      startClientY: event.clientY
    })
  }

  const handlePointerMove = (
    event: PointerEvent<HTMLButtonElement>,
    item: EditablePlannerItem
  ) => {
    if (!dragState || dragState.id !== item.id) return

    event.preventDefault()

    const gridTop = gridRef.current?.getBoundingClientRect().top ?? dragState.gridTop
    const { end, start } = getDragPreviewDates(
      event.clientY,
      gridTop,
      viewRange,
      dragState.durationMinutes
    )

    updateDragState({
      ...dragState,
      gridTop,
      moved: Math.abs(event.clientY - dragState.startClientY) >= 2,
      previewEnd: end.toISOString(),
      previewStart: start.toISOString()
    })
  }

  const persistMovedAcceptedItem = async (item: EditablePlannerItem) => {
    const spaceId = getPlannerEventSpaceId(item, payload)
    if (!spaceId || !item.eventId) return

    updateItem(item.id, (currentItem) => ({
      ...currentItem,
      error: null,
      saving: true
    }))

    try {
      const response = await authenticatedFetch(
        `/api/spaces/${spaceId}/events/${item.eventId}`,
        {
          body: JSON.stringify(getPlannerEventRequestBody(item, payload, item.eventId)),
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'PATCH'
        }
      )

      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || 'Planner move failed')
      }

      const eventId = getPersistedEventId(await response.json()) ?? item.eventId
      recentlyMovedEventIdsRef.current.set(eventId, Date.now() + 30_000)

      updateItem(item.id, (currentItem) => {
        const acceptedItem: EditablePlannerItem = {
          ...currentItem,
          end: item.end,
          error: null,
          eventId,
          saving: false,
          source: 'space',
          start: item.start,
          status: 'accepted'
        }

        return {
          ...acceptedItem,
          acceptedSnapshot: getPlannerItemSnapshot(acceptedItem, 'accepted')
        }
      })
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Planner move failed'

      updateItem(item.id, (currentItem) => {
        const acceptedSnapshot = currentItem.acceptedSnapshot

        return acceptedSnapshot
          ? {
              ...currentItem,
              ...acceptedSnapshot,
              acceptedSnapshot,
              error: message,
              saving: false
            }
          : {
              ...currentItem,
              error: message,
              saving: false
            }
      })
    }
  }

  const handlePointerUp = (
    event: PointerEvent<HTMLButtonElement>,
    item: EditablePlannerItem
  ) => {
    if (!dragState || dragState.id !== item.id) return

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const moved = dragState.moved || Math.abs(event.clientY - dragState.startClientY) >= 2

    if (moved) {
      const movedItem = itemsRef.current.find((currentItem) => currentItem.id === item.id) ?? item
      const gridTop = gridRef.current?.getBoundingClientRect().top ?? dragState.gridTop
      const { end, start } = getDragPreviewDates(
        event.clientY,
        gridTop,
        viewRange,
        dragState.durationMinutes
      )
      const previewItem: EditablePlannerItem = {
        ...movedItem,
        end: end.toISOString(),
        error: null,
        start: start.toISOString(),
        status: canPersistPlannerEvent(movedItem, payload) ? 'accepted' : 'proposed'
      }

      updateItem(item.id, () => previewItem)

      if (canPersistPlannerEvent(previewItem, payload) && previewItem.status === 'accepted') {
        void persistMovedAcceptedItem(previewItem)
      } else {
        suppressOpenForItemRef.current = item.id
        setActiveItemId(item.id)
      }
    }

    updateDragState(null)
  }

  const handlePointerCancel = (
    event: PointerEvent<HTMLButtonElement>,
    item: EditablePlannerItem
  ) => {
    if (!dragState || dragState.id !== item.id) return

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    updateDragState(null)
  }

  const cancelItem = (item: EditablePlannerItem) => {
    const acceptedSnapshot = item.acceptedSnapshot

    if (acceptedSnapshot) {
      updateItem(item.id, (currentItem) => ({
        ...currentItem,
        ...acceptedSnapshot,
        acceptedSnapshot,
        error: null,
        saving: false
      }))
      return
    }

    const initialItem = initialItemsById.get(item.id)

    if (initialItem?.status === 'accepted') {
      updateItem(item.id, () => initialItem)
      return
    }

    updateItem(item.id, (currentItem) => ({
      ...currentItem,
      error: null,
      saving: false,
      status: 'cancelled'
    }))
    setActiveItemId(null)
  }

  const deletePlannerEvent = async (item: EditablePlannerItem) => {
    const spaceId = getPlannerEventSpaceId(item, payload)
    if (!spaceId || !item.eventId) return

    updateItem(item.id, (currentItem) => ({
      ...currentItem,
      error: null,
      saving: true
    }))

    try {
      const response = await authenticatedFetch(
        `/api/spaces/${spaceId}/events/${item.eventId}`,
        {
          body: JSON.stringify({
            archived_at: new Date().toISOString()
          }),
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'PATCH'
        }
      )

      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || 'Could not delete this event')
      }

      updateItem(item.id, (currentItem) => ({
        ...currentItem,
        error: null,
        saving: false,
        status: 'cancelled'
      }))
      setActiveItemId(null)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Could not delete this event'

      updateItem(item.id, (currentItem) => ({
        ...currentItem,
        error: message,
        saving: false
      }))
    }
  }

  const approveItem = async (item: EditablePlannerItem) => {
    const spaceId = item.spaceId ?? payload.spaceId
    // An item tied to a real task/event needs a Space id to actually
    // persist. Without one, silently marking it "accepted" would show a
    // green success state while never saving anything anywhere.
    const requiresSpacePersistence = Boolean(item.taskId || item.eventId)

    updateItem(item.id, (currentItem) => ({
      ...currentItem,
      error: null,
      saving: true
    }))

    if (!spaceId) {
      if (requiresSpacePersistence) {
        updateItem(item.id, (currentItem) => ({
          ...currentItem,
          error: 'Missing Space id for this item, so it could not be saved.',
          saving: false
        }))
        return false
      }

      updateItem(item.id, (currentItem) => {
        const acceptedItem: EditablePlannerItem = {
          ...currentItem,
          error: null,
          saving: false,
          status: 'accepted'
        }

        return {
          ...acceptedItem,
          acceptedSnapshot: getPlannerItemSnapshot(acceptedItem, 'accepted')
        }
      })
      return true
    }

    try {
      let eventId = item.eventId
      const body = getPlannerEventRequestBody(item, payload, eventId)
      const response = await authenticatedFetch(
        eventId
          ? `/api/spaces/${spaceId}/events/${eventId}`
          : `/api/spaces/${spaceId}/events`,
        {
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json'
          },
          method: eventId ? 'PATCH' : 'POST'
        }
      )

      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || 'Planner update failed')
      }

      eventId = eventId ?? getPersistedEventId(await response.json())

      updateItem(item.id, (currentItem) => {
        const acceptedItem: EditablePlannerItem = {
          ...currentItem,
          error: null,
          ...(eventId ? { eventId } : {}),
          saving: false,
          source: 'space',
          status: 'accepted'
        }

        return {
          ...acceptedItem,
          acceptedSnapshot: getPlannerItemSnapshot(acceptedItem, 'accepted')
        }
      })
      return true
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Planner update failed'

      updateItem(item.id, (currentItem) => ({
        ...currentItem,
        error: message,
        saving: false
      }))
      return false
    }
  }

  const approveAllItems = async () => {
    const itemsToApprove = items.filter(
      (item) => item.status === 'proposed' && !isReadOnlyItem(item)
    )
    if (itemsToApprove.length === 0 || approvingAll) return

    setApprovingAll(true)

    try {
      for (const item of itemsToApprove) {
        await approveItem(item)
      }
    } finally {
      setApprovingAll(false)
    }
  }

  return (
    <section
      className={cn(
        'not-prose border-border/70 bg-background text-foreground my-4 overflow-hidden rounded-2xl border shadow-[0_18px_48px_-36px_rgba(15,23,42,0.45)]',
        className
      )}
      data-testid="losi-planner-day-card"
    >
      <div className="border-border/70 bg-muted/20 border-b px-4 py-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="border-border/70 bg-background inline-flex size-8 items-center justify-center rounded-full border shadow-sm">
                <IconCalendarTime aria-hidden="true" className="size-4 text-amber-600" />
              </span>
              <div className="min-w-0">
                <h3 className="truncate text-sm font-semibold">
                  {payload.title || 'Day plan'}
                </h3>
                <p className="text-muted-foreground text-xs">
                  {formatDayLabel(dayDate)} · {getViewRangeLabel(viewRange)}
                </p>
              </div>
            </div>
            {payload.subtitle ? (
              <p className="text-muted-foreground mt-2 max-w-2xl text-sm leading-5">
                {payload.subtitle}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <span className="rounded-full border border-amber-300/70 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-100">
              {proposedCount} draft
            </span>
            <span className="rounded-full border border-emerald-300/70 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-100">
              {acceptedCount} set
            </span>
            {approvableCount > 0 ? (
              <Button
                aria-label={[
                  `Approve ${approvableCount} draft planner`,
                  `item${approvableCount === 1 ? '' : 's'}`
                ].join(' ')}
                className="h-8 rounded-full px-3"
                disabled={approvingAll}
                onClick={() => void approveAllItems()}
                type="button"
              >
                <IconCheck aria-hidden="true" className="size-4" />
                {approvingAll ? 'Approving' : 'Approve all'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {allDayItems.length > 0 ? (
        <div className="border-border/70 grid grid-cols-[64px_minmax(0,1fr)] border-b">
          <div className="border-border/70 bg-muted/20 text-muted-foreground border-r px-3 py-2 text-[11px] font-medium">
            All day
          </div>
          <div className="flex min-w-0 flex-wrap gap-2 p-2">
            {allDayItems.map((item) => (
              <PlannerItemPill
                canDelete={canDeletePlannerEvent(item, payload)}
                item={item}
                key={item.id}
                onApprove={() => void approveItem(item)}
                onCancel={() => cancelItem(item)}
                onDelete={() => void deletePlannerEvent(item)}
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-[64px_minmax(0,1fr)]">
        <div className="border-border/70 bg-muted/20 border-r">
          {viewRange.timeSlots.map((slot) => (
            <div
              className="border-border/60 h-16 border-t px-2 pt-1.5 first:border-t-0"
              key={slot.toISOString()}
            >
              <span className="text-muted-foreground text-[11px] font-medium">
                {formatRangeTime(slot, viewRange.start)}
              </span>
            </div>
          ))}
        </div>

        <div
          className="bg-background relative isolate overflow-hidden"
          ref={gridRef}
          style={{ height: gridHeight }}
        >
          {viewRange.timeSlots.map((slot) => (
            <div
              aria-hidden="true"
              className="border-border/55 h-16 border-t first:border-t-0"
              key={slot.toISOString()}
            />
          ))}

          {currentTimeTop !== null ? (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 z-[1] flex items-center"
              style={{ top: currentTimeTop }}
            >
              <span className="ml-[-5px] size-2.5 rounded-full bg-rose-500 shadow-sm" />
              <span className="h-px flex-1 bg-rose-500/80" />
              <span className="mr-2 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm">
                Now
              </span>
            </div>
          ) : null}

          {timedItems.length === 0 ? (
            <div className="text-muted-foreground absolute inset-0 flex items-center justify-center px-6 text-center text-sm">
              Nothing is placed on this day.
            </div>
          ) : null}

          {stackedItems.map((stackedItem) => {
            const item = stackedItem.item
            const isDraggedItem = dragState?.id === item.id && dragState.moved
            const renderedItem: EditablePlannerItem = isDraggedItem
              ? {
                  ...item,
                  end: dragState.previewEnd,
                  start: dragState.previewStart,
                  status: canPersistPlannerEvent(item, payload) ? 'accepted' : 'proposed'
                }
              : item
            const itemStyle = getItemStyle(
              renderedItem,
              viewRange,
              stackedItemsById.get(item.id)
            )
            if (!itemStyle) return null

            return (
              <PlannerTimedItem
                active={activeItemId === item.id}
                dayDate={dayDate}
                dragState={dragState}
                item={renderedItem}
                itemStyle={itemStyle}
                key={item.id}
                canDelete={canDeletePlannerEvent(item, payload)}
                onApprove={() => void approveItem(item)}
                onCancel={() => cancelItem(item)}
                onDelete={() => void deletePlannerEvent(item)}
                onOpenChange={(open) => {
                  if (suppressOpenForItemRef.current === item.id) {
                    suppressOpenForItemRef.current = null
                    return
                  }

                  setActiveItemId(open ? item.id : null)
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerCancel={handlePointerCancel}
                onPointerUp={handlePointerUp}
                onUpdate={(updater) => updateItem(item.id, updater)}
                onEditAsDraft={() =>
                  updateItem(item.id, (currentItem) => ({
                    ...currentItem,
                    error: null,
                    status: 'proposed'
                  }))
                }
              />
            )
          })}
        </div>
      </div>
    </section>
  )
}

function PlannerItemPill({
  canDelete,
  item,
  onApprove,
  onCancel,
  onDelete
}: {
  canDelete: boolean
  item: EditablePlannerItem
  onApprove: () => void
  onCancel: () => void
  onDelete: () => void
}) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-2 rounded-full border px-2.5 py-1 text-xs shadow-sm',
        sourceStyles[item.status],
        item.error && 'border-destructive/50'
      )}
      title={item.error ?? undefined}
    >
      <span className="truncate font-medium">{item.title}</span>
      {item.error ? (
        <span aria-label={item.error} className="text-destructive shrink-0" role="alert">
          <IconX aria-hidden="true" className="size-3.5" />
        </span>
      ) : null}
      {item.status === 'proposed' ? (
        <span className="flex shrink-0 items-center gap-1">
          <button
            aria-label={`Approve ${item.title}`}
            className="bg-background/85 inline-flex size-6 items-center justify-center rounded-full text-emerald-700 shadow-sm transition-transform active:scale-[0.96]"
            data-planner-action
            onClick={onApprove}
            type="button"
          >
            <IconCheck aria-hidden="true" className="size-3.5" />
          </button>
          <button
            aria-label={`Cancel ${item.title}`}
            className="bg-background/85 text-muted-foreground inline-flex size-6 items-center justify-center rounded-full shadow-sm transition-transform active:scale-[0.96]"
            data-planner-action
            onClick={onCancel}
            type="button"
          >
            <IconX aria-hidden="true" className="size-3.5" />
          </button>
        </span>
      ) : null}
      {item.status === 'accepted' && canDelete ? (
        <button
          aria-label={`Delete ${item.title}`}
          className="bg-background/85 text-destructive inline-flex size-6 items-center justify-center rounded-full shadow-sm transition-transform active:scale-[0.96]"
          data-planner-action
          disabled={item.saving}
          onClick={onDelete}
          type="button"
        >
          <IconTrash aria-hidden="true" className="size-3.5" />
        </button>
      ) : null}
    </span>
  )
}

function PlannerTimedItem({
  active,
  canDelete,
  dayDate,
  dragState,
  item,
  itemStyle,
  onApprove,
  onCancel,
  onDelete,
  onEditAsDraft,
  onOpenChange,
  onPointerCancel,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onUpdate
}: {
  active: boolean
  canDelete: boolean
  dayDate: Date
  dragState: DragState | null
  item: EditablePlannerItem
  itemStyle: PlannerItemStyle
  onApprove: () => void
  onCancel: () => void
  onDelete: () => void
  onEditAsDraft: () => void
  onOpenChange: (open: boolean) => void
  onPointerCancel: (event: PointerEvent<HTMLButtonElement>, item: EditablePlannerItem) => void
  onPointerDown: (event: PointerEvent<HTMLButtonElement>, item: EditablePlannerItem) => void
  onPointerMove: (event: PointerEvent<HTMLButtonElement>, item: EditablePlannerItem) => void
  onPointerUp: (event: PointerEvent<HTMLButtonElement>, item: EditablePlannerItem) => void
  onUpdate: (updater: (item: EditablePlannerItem) => EditablePlannerItem) => void
}) {
  const startDate = new Date(item.start)
  const endDate = new Date(item.end)
  const isDragging = dragState?.id === item.id && dragState.moved
  const isNarrow = typeof itemStyle.width === 'string' && Number.parseFloat(itemStyle.width) < 30
  const isCompact = itemStyle.height < 52 || isNarrow
  const isTiny = itemStyle.height < 24
  const showTimeLabel = !isTiny && !isNarrow
  const showInlineActions = item.status === 'proposed' && active && !isCompact
  const timeLabel = formatTimeRange(startDate, endDate)
  const actionButtonClass = cn(
    'bg-background/90 text-muted-foreground inline-flex items-center justify-center rounded-full shadow-sm transition-transform hover:scale-105 active:scale-[0.96]',
    isCompact ? 'size-5' : 'size-7'
  )

  return (
    <Popover open={active} onOpenChange={onOpenChange}>
      <div
        className={cn('absolute z-[2]', active && 'z-[4]', isDragging && 'z-[5]')}
        style={{
          height: itemStyle.height,
          left: itemStyle.left,
          top: itemStyle.top,
          width: itemStyle.width
        }}
      >
        <PopoverTrigger asChild>
          <button
            aria-label={`${item.title}, ${timeLabel}`}
            className={cn(
              'relative size-full touch-none overflow-hidden rounded-xl border px-2.5 py-2 text-left shadow-sm outline-none transition-[box-shadow,transform,border-color]',
              'focus-visible:ring-ring focus-visible:ring-2',
              !isReadOnlyItem(item) && 'cursor-grab active:cursor-grabbing',
              sourceStyles[item.status],
              item.status === 'proposed' && 'ring-1 ring-amber-300/70',
              showInlineActions && 'pr-14',
              isCompact && 'rounded-lg px-2 py-1',
              isTiny && 'rounded-md px-1.5 py-0',
              active && 'ring-ring/30 shadow-lg ring-2',
              isDragging && 'scale-[1.01] shadow-xl'
            )}
            onPointerDown={(event) => onPointerDown(event, item)}
            onPointerMove={(event) => onPointerMove(event, item)}
            onPointerCancel={(event) => onPointerCancel(event, item)}
            onPointerUp={(event) => onPointerUp(event, item)}
            type="button"
          >
            <span className="flex min-w-0 items-start gap-2">
              <IconGripVertical
                aria-hidden="true"
                className={cn(
                  'text-muted-foreground mt-0.5 size-3.5 shrink-0',
                  isTiny ? 'hidden' : isCompact && 'hidden sm:block',
                  isReadOnlyItem(item) && 'opacity-30'
                )}
              />
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    'block truncate font-semibold',
                    isCompact ? 'text-[11px]' : 'text-xs'
                  )}
                >
                  {item.title}
                </span>
                {showTimeLabel ? (
                  <span
                    className={cn(
                      'flex min-w-0 items-center gap-1 opacity-80',
                      isCompact ? 'text-[10px]' : 'mt-0.5 text-[11px]'
                    )}
                  >
                    <IconClock aria-hidden="true" className="size-3 shrink-0" />
                    <span className="truncate">
                      {timeLabel}
                    </span>
                  </span>
                ) : null}
                {item.location && !isCompact ? (
                  <span className="mt-0.5 flex min-w-0 items-center gap-1 text-[11px] opacity-75">
                    <IconMapPin aria-hidden="true" className="size-3 shrink-0" />
                    <span className="truncate">{item.location}</span>
                  </span>
                ) : null}
              </span>
              {!isCompact && !isNarrow ? (
                <span
                  aria-label={statusCopy[item.status]}
                  className={cn(
                    'inline-flex size-5 shrink-0 items-center justify-center rounded-full shadow-sm',
                    statusIconStyles[item.status]
                  )}
                  title={statusCopy[item.status]}
                >
                  {getPlannerStatusIcon(item.status, 'size-3')}
                </span>
              ) : null}
            </span>
          </button>
        </PopoverTrigger>

        {showInlineActions ? (
          <span className="absolute right-1.5 top-1.5 flex items-center gap-1">
            <button
              aria-label={`Approve ${item.title}`}
              className={cn(actionButtonClass, 'text-emerald-700')}
              data-planner-action
              disabled={item.saving}
              onClick={(event) => {
                event.stopPropagation()
                onApprove()
              }}
              type="button"
            >
              <IconCheck aria-hidden="true" className={isCompact ? 'size-3' : 'size-4'} />
            </button>
            <button
              aria-label={`Cancel ${item.title}`}
              className={actionButtonClass}
              data-planner-action
              disabled={item.saving}
              onClick={(event) => {
                event.stopPropagation()
                onCancel()
              }}
              type="button"
            >
              <IconX aria-hidden="true" className={isCompact ? 'size-3' : 'size-4'} />
            </button>
          </span>
        ) : null}
      </div>

      <PopoverContent
        align="start"
        className="w-[min(20rem,calc(100vw-2rem))] rounded-xl p-0 shadow-xl"
        side="right"
      >
        <div className="border-b px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="bg-muted inline-flex size-7 items-center justify-center rounded-full">
              <IconPencil aria-hidden="true" className="size-3.5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{item.title}</p>
              <p className="text-muted-foreground text-xs">{statusCopy[item.status]}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3 p-3">
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor={`${item.id}-title`}>
              Title
            </Label>
            <Input
              id={`${item.id}-title`}
              onChange={(event) =>
                onUpdate((currentItem) => ({
                  ...currentItem,
                  title: event.target.value,
                  status: 'proposed'
                }))
              }
              value={item.title}
            />
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs" htmlFor={`${item.id}-start`}>
                Start
              </Label>
              <Input
                id={`${item.id}-start`}
                onChange={(event) =>
                  onUpdate((currentItem) =>
                    setItemTime(currentItem, dayDate, 'start', event.target.value)
                  )
                }
                type="time"
                value={toTimeInputValue(item.start)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs" htmlFor={`${item.id}-end`}>
                End
              </Label>
              <Input
                id={`${item.id}-end`}
                onChange={(event) =>
                  onUpdate((currentItem) =>
                    setItemTime(currentItem, dayDate, 'end', event.target.value)
                  )
                }
                type="time"
                value={toTimeInputValue(item.end)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor={`${item.id}-location`}>
              Location
            </Label>
            <Input
              id={`${item.id}-location`}
              onChange={(event) =>
                onUpdate((currentItem) => ({
                  ...currentItem,
                  location: event.target.value,
                  status: 'proposed'
                }))
              }
              placeholder="Optional"
              value={item.location ?? ''}
            />
          </div>

          {item.error ? (
            <p className="border-destructive/30 bg-destructive/10 text-destructive rounded-lg border px-2.5 py-2 text-xs">
              {item.error}
            </p>
          ) : null}

          {item.status === 'accepted' ? (
            <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-2 text-xs font-medium text-emerald-700 dark:text-emerald-200">
                <IconCheck aria-hidden="true" className="size-4 shrink-0" />
                {item.eventId || item.taskId ? 'Saved' : 'Kept in this plan (not saved to a Space)'}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                {canDelete ? (
                  <Button
                    className="h-9 w-full rounded-full px-3 sm:w-auto"
                    disabled={item.saving}
                    onClick={onDelete}
                    type="button"
                    variant="destructive"
                  >
                    <IconTrash aria-hidden="true" className="mr-1 size-4" />
                    Delete
                  </Button>
                ) : null}
                <Button
                  className="h-9 w-full rounded-full px-3 sm:w-auto"
                  disabled={item.saving}
                  onClick={onEditAsDraft}
                  type="button"
                  variant="outline"
                >
                  Edit
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center sm:justify-between">
              <Button
                className="h-9 w-full rounded-full px-3 sm:w-auto"
                disabled={item.saving}
                onClick={onCancel}
                type="button"
                variant="outline"
              >
                <IconX aria-hidden="true" className="mr-1 size-4" />
                {item.acceptedSnapshot ? 'Undo changes' : 'Cancel'}
              </Button>
              <Button
                className="h-9 w-full rounded-full px-3 sm:w-auto"
                disabled={item.saving}
                onClick={onApprove}
                type="button"
              >
                <IconCheck aria-hidden="true" className="mr-1 size-4" />
                {item.saving ? 'Saving' : 'Approve'}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default LosiPlannerDayCard
